'use strict';

const libSampleTestRules = require('./SampleTest-Rules.js');

/**
 * @typedef {Object} SampleRecordReader
 * @property {(pEntity: String, pFilter: String) => Promise<Array<Object>>} getEntitySet - Every record of an entity
 *   matching a meadow filter, across pages; rejects on a failed read.
 */

/**
 * Everything connected to one sample, loaded on first ask and kept until invalidate(). Get one from
 * HeadlightSamples.forSample, which hands every caller the same scope for a sample, so screens showing the same
 * sample agree; reads go through the provider's REST client.
 */
class SampleScope
{
	/**
	 * @param {SampleRecordReader} pReader
	 * @param {number} pIDSample
	 */
	constructor(pReader, pIDSample)
	{
		this.reader = pReader;
		this.IDSample = Number(pIDSample) || 0;
		/** @type {?Promise<import('./SampleTest-Rules.js').TestBundle>} */
		this._testBundle = null;
		/** @type {?import('./SampleTest-Rules.js').TestBundle} - the test bundle once it has loaded */
		this.loadedTestBundle = null;
	}

	/**
	 * Drop what has been loaded so the next ask reads it again.
	 */
	invalidate()
	{
		this._testBundle = null;
		this.loadedTestBundle = null;
	}

	/**
	 * Whether the loaded test bundle has a test instance; false until the bundle has loaded.
	 *
	 * @param {number} pIDTestInstance
	 * @return {boolean}
	 */
	hasLoadedTestInstance(pIDTestInstance)
	{
		const tmpMap = this.loadedTestBundle && this.loadedTestBundle.TestInstanceDocumentMap;
		return !!tmpMap && Object.prototype.hasOwnProperty.call(tmpMap, String(Number(pIDTestInstance) || 0));
	}

	/**
	 * The sample's test instances, their tests and the documents linked to each (see SampleTest-Rules buildTestBundle).
	 * A failed read rejects, and the next call tries again.
	 *
	 * @return {Promise<import('./SampleTest-Rules.js').TestBundle>}
	 */
	getTestBundle()
	{
		if (!this._testBundle)
		{
			const tmpLoad = this._loadTestBundle();
			this._testBundle = tmpLoad;
			tmpLoad.then((pBundle) =>
			{
				if (this._testBundle === tmpLoad)
				{
					this.loadedTestBundle = pBundle;
				}
			}, () =>
			{
				if (this._testBundle === tmpLoad)
				{
					this._testBundle = null;
				}
			});
		}
		return this._testBundle;
	}

	/**
	 * One row per test with its result, remarks and linked documents' form statuses, pending form values first (see
	 * SampleTest-Rules buildTestSummary).
	 *
	 * @return {Promise<Array<import('./SampleTest-Rules.js').TestSummaryRow>>}
	 */
	async getTestSummary()
	{
		return libSampleTestRules.buildTestSummary(await this.getTestBundle());
	}

	/**
	 * Every record of an entity whose `pField` is in `pIDs`; no read for an empty list, which the server would take as
	 * `IN ('')` and answer with ID 0 rows.
	 *
	 * @param {String} pEntity
	 * @param {String} pField
	 * @param {Array<number>} pIDs
	 * @param {String} [pExtraFilter] - more meadow filter terms, prefixed with `~`
	 * @return {Promise<Array<Object>>}
	 */
	async _getByIDs(pEntity, pField, pIDs, pExtraFilter)
	{
		const tmpIDs = [ ...new Set((pIDs || []).map(Number).filter((pID) => pID > 0)) ];
		if (!tmpIDs.length)
		{
			return [];
		}
		return (await this.reader.getEntitySet(pEntity, `FBL~${pField}~INN~${tmpIDs.join(',')}${pExtraFilter || ''}`)) || [];
	}

	/**
	 * @return {Promise<import('./SampleTest-Rules.js').TestBundle>}
	 */
	async _loadTestBundle()
	{
		if (!this.IDSample)
		{
			return libSampleTestRules.buildTestBundle({});
		}
		const tmpInstanceJoins = (await this.reader.getEntitySet('TestInstanceSampleJoin', `FBV~IDSample~EQ~${this.IDSample}`)) || [];
		const tmpTestInstances = await this._getByIDs('TestInstance', 'IDTestInstance', tmpInstanceJoins.map((pJoin) => pJoin.IDTestInstance));
		if (!tmpTestInstances.length)
		{
			return libSampleTestRules.buildTestBundle({});
		}
		const [ tmpTests, tmpTestInstanceDocumentJoins, tmpSampleDocumentJoins ] = await Promise.all(
			[
				this._getByIDs('Test', 'IDTest', tmpTestInstances.map((pInstance) => pInstance.IDTest)),
				this._getByIDs('DocumentPolyJoin', 'IDRemote', tmpTestInstances.map((pInstance) => pInstance.IDTestInstance), '~FBV~RemoteType~EQ~TestInstance'),
				this.reader.getEntitySet('DocumentPolyJoin', `FBV~RemoteType~EQ~Sample~FBV~IDRemote~EQ~${this.IDSample}`),
			]);
		const tmpDocuments = await this._getByIDs('Document', 'IDDocument', (tmpTestInstanceDocumentJoins || []).concat(tmpSampleDocumentJoins || []).map((pJoin) => pJoin.IDDocument));
		return libSampleTestRules.buildTestBundle(
			{
				TestInstances: tmpTestInstances,
				Tests: tmpTests,
				TestInstanceDocumentJoins: tmpTestInstanceDocumentJoins,
				SampleDocumentJoins: tmpSampleDocumentJoins || [],
				Documents: tmpDocuments,
			});
	}
}

module.exports = SampleScope;
